import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import SaveStateIndicator, { SaveStatus } from './SaveStateIndicator';
import { loadCanvas, saveCanvas } from '../../services/tutorSessionCanvasService';
import { aiCanvasNodeExtensions } from '../../extensions/tiptapAiNodes';
import { gnNodeExtensions } from '../../extensions/tiptapGnNodes';
import { isLikelyQuestionOrProblem } from '../../utils/pasteQuestionDetector';
import { parseVideoUrl } from '../../utils/videoUrlParser';

export interface RichTextCanvasRef {
  loadContent: (json: object) => void;
  /** Appends one new node at the end of the document — used by the streaming AI actions (T045). */
  appendBlock: (blockType: 'TEXT' | 'IMAGE' | 'CHART', attrs: Record<string, any>) => void;
  /** Inserts an arbitrary GN node (videoEmbed, mediaClip, inkBlock, mathBlock, pageBreak) at the cursor (spec 046). */
  insertGnNode: (nodeType: string, attrs: Record<string, any>) => void;
  /** Direct access for GN view/page logic (spec 046 US3). */
  getEditor: () => import('@tiptap/react').Editor | null;
}

interface RichTextCanvasProps {
  courseId: number;
  nodeId: number;
  onSaveStateChange?: (status: SaveStatus) => void;
  /** Fired when pasted text looks like a question/problem (spec 044 FR-006). */
  onPasteDetected?: (text: string, position: { top: number; left: number }) => void;
  /** Fired after an image is pasted onto the page (spec 046 FR-001/US2 image cues). */
  onImagePasted?: (imageDataUrl: string, position: { top: number; left: number }) => void;
  /**
   * Spec 046: pluggable persistence so GnNotebook can host tutor (content_pages) and
   * student (student_gn_canvas) documents through the same editor. Defaults preserve the
   * original spec 042/044 tutor behavior.
   */
  loadFn?: () => Promise<string | null>;
  saveFn?: (blocks: string) => Promise<void>;
  /** Fired on every document change with the serialized doc (GN cue observer, spec 046 US2). */
  onDocChanged?: (blocks: string) => void;
}

const DEBOUNCE_MS = 3000;

const NODE_TYPE_BY_BLOCK_TYPE: Record<string, string> = {
  TEXT: 'aiTextBlock',
  IMAGE: 'imageBlock',
  CHART: 'chartBlock',
};

const RichTextCanvas = forwardRef<RichTextCanvasRef, RichTextCanvasProps>(
  ({ courseId, nodeId, onSaveStateChange, onPasteDetected, onImagePasted, loadFn, saveFn, onDocChanged }, ref) => {
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestBlocksRef = useRef<string>('[]');
    const onPasteDetectedRef = useRef(onPasteDetected);
    onPasteDetectedRef.current = onPasteDetected;
    const onImagePastedRef = useRef(onImagePasted);
    onImagePastedRef.current = onImagePasted;

    const notifyStatus = useCallback((status: SaveStatus) => {
      setSaveStatus(status);
      onSaveStateChange?.(status);
    }, [onSaveStateChange]);

    const doSave = useCallback(
      (blocks: string) => (saveFn ? saveFn(blocks) : saveCanvas(courseId, nodeId, blocks)),
      [saveFn, courseId, nodeId]
    );

    const persistCanvas = useCallback(async (blocks: string) => {
      notifyStatus('saving');
      try {
        await doSave(blocks);
        notifyStatus('saved');
      } catch {
        notifyStatus('error');
        setTimeout(() => {
          doSave(latestBlocksRef.current)
            .then(() => notifyStatus('saved'))
            .catch(() => notifyStatus('error'));
        }, 5000);
      }
    }, [doSave, notifyStatus]);

    const editor = useEditor({
      extensions: [StarterKit, ...aiCanvasNodeExtensions, ...gnNodeExtensions],
      content: '',
      onUpdate: ({ editor: e }) => {
        const json = JSON.stringify(e.getJSON());
        latestBlocksRef.current = json;
        onDocChanged?.(json);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => persistCanvas(json), DEBOUNCE_MS);
      },
      editorProps: {
        handlePaste: (view, event) => {
          const text = event.clipboardData?.getData('text/plain') || '';
          // Spec 046 FR-003: a pasted video link becomes an embedded player.
          const video = parseVideoUrl(text);
          if (video) {
            const nodeType = view.state.schema.nodes.videoEmbed;
            if (nodeType) {
              const node = nodeType.create({
                provider: video.provider, videoId: video.videoId, originalUrl: video.originalUrl,
              });
              view.dispatch(view.state.tr.replaceSelectionWith(node));
              return true;
            }
          }
          // Spec 046 FR-001: a pasted image is embedded on the page at that position.
          const imageItem = Array.from(event.clipboardData?.items || [])
            .find(item => item.type.startsWith('image/'));
          if (imageItem) {
            const file = imageItem.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = () => {
                if (view.isDestroyed) return;
                const dataUrl = reader.result as string;
                const nodeType = view.state.schema.nodes.imageBlock;
                if (nodeType) {
                  const node = nodeType.create({ url: dataUrl, alt: '', origin: 'STUDENT' });
                  view.dispatch(view.state.tr.replaceSelectionWith(node));
                }
                const coords = view.coordsAtPos(view.state.selection.to);
                onImagePastedRef.current?.(dataUrl, { top: coords.bottom, left: coords.left });
              };
              reader.readAsDataURL(file);
              return true;
            }
          }
          if (text && isLikelyQuestionOrProblem(text)) {
            setTimeout(() => {
              if (view.isDestroyed) return;
              const coords = view.coordsAtPos(view.state.selection.to);
              onPasteDetectedRef.current?.(text, { top: coords.bottom, left: coords.left });
            }, 0);
          }
          return false;
        },
      },
    });

    useImperativeHandle(ref, () => ({
      loadContent: (json: object) => {
        if (!editor || editor.isDestroyed) return;
        editor.commands.setContent(json);
        const blocks = JSON.stringify(editor.getJSON());
        latestBlocksRef.current = blocks;
        onDocChanged?.(blocks); // setContent doesn't reliably fire onUpdate — notify explicitly
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => persistCanvas(blocks), DEBOUNCE_MS);
      },
      appendBlock: (blockType, attrs) => {
        if (!editor || editor.isDestroyed) return;
        const nodeType = NODE_TYPE_BY_BLOCK_TYPE[blockType] || 'aiTextBlock';
        editor.commands.insertContentAt(editor.state.doc.content.size, { type: nodeType, attrs });
      },
      insertGnNode: (nodeType, attrs) => {
        if (!editor || editor.isDestroyed) return;
        editor.commands.insertContent({ type: nodeType, attrs });
      },
      getEditor: () => (editor && !editor.isDestroyed ? editor : null),
    }), [editor, persistCanvas]);

    // Load initial canvas content
    useEffect(() => {
      if (!editor) return;
      (loadFn ? loadFn() : loadCanvas(courseId, nodeId))
        .then((blocks) => {
          if (blocks && editor && !editor.isDestroyed) {
            try {
              editor.commands.setContent(JSON.parse(blocks));
            } catch {
              editor.commands.setContent(blocks);
            }
            // setContent doesn't reliably fire onUpdate — notify explicitly so hosts that
            // derive page/cue state from the doc (spec 046 GnNotebook) see the loaded content.
            onDocChanged?.(JSON.stringify(editor.getJSON()));
          }
        })
        .catch(() => {});
      // loadFn must be memoized by callers (GnNotebook) to avoid re-loading on every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, courseId, nodeId, loadFn]);

    // Flush pending save on unmount
    useEffect(() => {
      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
          persistCanvas(latestBlocksRef.current);
        }
      };
    }, [persistCanvas]);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1, minHeight: 20 }}>
          <SaveStateIndicator status={saveStatus} />
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 3,
            py: 2,
            '& .ProseMirror': {
              outline: 'none',
              minHeight: '100%',
              fontSize: '1rem',
              lineHeight: 1.7,
            },
            '& .ProseMirror p': { margin: '0.5em 0' },
          }}
        >
          <EditorContent editor={editor} style={{ height: '100%' }} />
        </Box>
      </Box>
    );
  }
);

RichTextCanvas.displayName = 'RichTextCanvas';

export default RichTextCanvas;
